import { useCallback, useReducer, useRef } from 'react'
import { urlApi } from '../../config'
import { logout } from '../../auth'
import useAuth from '../../hooks/useAuth'

const REVIEW_CARD_LOOKAHEAD = 2

export default function useReviewCardCache() {
  const { getAccessTokenSilently } = useAuth()
  const cacheRef = useRef(new Map())
  const inFlightRef = useRef(new Set())
  const [, bumpVersion] = useReducer(count => count + 1, 0)

  const fetchCard = useCallback(
    async paymentId => {
      if (!paymentId) return
      const existing = cacheRef.current.get(paymentId)
      if (existing && existing.status === 'ready') return
      if (inFlightRef.current.has(paymentId)) return

      inFlightRef.current.add(paymentId)
      cacheRef.current.set(paymentId, { status: 'loading', data: null })
      bumpVersion()

      try {
        const token = await getAccessTokenSilently()
        const info = { credentials: 'include', headers: {} }
        if (typeof token === 'string') {
          info.headers.authorization = `Bearer ${token}`
        }
        const response = await fetch(`${urlApi}/admin/driver-payments/review/${paymentId}`, info)
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            await logout({ returnTo: window.location.href })
          }
          cacheRef.current.set(paymentId, { status: 'error', data: null })
          return
        }
        const json = await response.json()
        cacheRef.current.set(paymentId, { status: 'ready', data: json })
      } catch (e) {
        cacheRef.current.set(paymentId, { status: 'error', data: null })
      } finally {
        inFlightRef.current.delete(paymentId)
        bumpVersion()
      }
    },
    [getAccessTokenSilently]
  )

  const prefetchFrom = useCallback(
    (ids, startIndex) => {
      if (!Array.isArray(ids) || !ids.length) return
      const last = Math.min(startIndex + REVIEW_CARD_LOOKAHEAD, ids.length - 1)
      for (let i = startIndex; i <= last; i++) {
        fetchCard(ids[i])
      }
    },
    [fetchCard]
  )

  const getEntry = useCallback(paymentId => cacheRef.current.get(paymentId), [])

  return { prefetchFrom, getEntry, fetchCard }
}

export { REVIEW_CARD_LOOKAHEAD }
